import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Badge, Alert } from "react-bootstrap";
import axios from "axios";
import { Authorization } from "../Context/Authorization";
import { toast } from "react-toastify";
import Preloader from "../shared/PreLoaders";

const Api = process.env.REACT_APP_API;

const RangeApproval = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [selectedVfcs, setSelectedVfcs] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [rangeData, setRangeData] = useState([]);
  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [selectedJfmcType, setSelectedJfmcType] = useState(null);

  // ✅ Default to previous month
  const getPreviousMonth = () => {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    return `${year}-${month.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (open) {
      setSelectedMonth(getPreviousMonth());
    }
  }, [open]);
  const formatSelectedMonth = () => {
    if (!selectedMonth) return "";
    const date = new Date(`${selectedMonth}-01`);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  // ✅ Fetch VFC data
  useEffect(() => {
    if (!open || !selectedMonth) return;

    const fetchVfcStatus = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(
          `${Api}/api/declaration/range/vfcs?month=${selectedMonth}`,
          config
        );

        const ranges = res.data.ranges || [];

        setRangeData(ranges);

        // 🔥 Merge JFMC Types across all ranges (REMOVE DUPLICATES)

        const typeMap = {};

        ranges.forEach((range) => {
          range.jfmcTypes.forEach((type) => {
            if (!typeMap[type.jfmcTypeId]) {
              typeMap[type.jfmcTypeId] = {
                jfmcTypeId: type.jfmcTypeId,
                jfmcTypeName: type.jfmcTypeName,
                vfcs: [],
              };
            }

            // Merge VFCs
            typeMap[type.jfmcTypeId].vfcs.push(...type.vfcs);
          });
        });

        const uniqueTypes = Object.values(typeMap);

        setJfmcTypes(uniqueTypes);

        // Default select first
        if (uniqueTypes.length > 0) {
          setSelectedJfmcType(uniqueTypes[0]);
        }

      } catch (error) {
        console.error("Error fetching VFCs:", error);
        toast.error("Failed to load VFC data");
      } finally {
        setLoading(false);
      }
    };

    fetchVfcStatus();
  }, [open, selectedMonth]);

  const vfcData = selectedJfmcType?.vfcs || [];

  const alreadySubmittedVfcs = vfcData
    .filter((v) => v.submitted)
    .map((v) => v._id);

  const allSubmitted =
    vfcData.length > 0 &&
    vfcData.every((v) => v.submitted);

  // ✅ Checkbox toggle (individual)
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    if (allSubmitted) return; // prevent change when all are submitted

    setSelectedVfcs((prev) =>
      checked ? [...prev, value] : prev.filter((id) => id !== value)
    );
  };

  // ✅ Select All toggle (only selects unsubmitted VFCs)
  const handleSelectAll = (e) => {
    const { checked } = e.target;
    if (allSubmitted) return; // prevent changing when fully submitted

    if (checked) {
      const allUnsubmitted = vfcData
        .filter((vfc) => !vfc.submitted)
        .map((vfc) => vfc._id);
      setSelectedVfcs([...alreadySubmittedVfcs, ...allUnsubmitted]);
    } else {
      setSelectedVfcs([...alreadySubmittedVfcs]);
    }
  };

  // ✅ Check if all unsubmitted VFCs are selected
  const unsubmittedVfcs = vfcData.filter((vfc) => !vfc.submitted);
  const allSelected =
    unsubmittedVfcs.length > 0 &&
    unsubmittedVfcs.every((vfc) => selectedVfcs.includes(vfc._id));

  // ✅ Submit handler
  const handleSubmit = async () => {
    if (selectedVfcs.length === 0) {
      toast.warning("Please select at least one unsubmitted VFC.");
      return;
    }

    if (allSubmitted) {
      toast.info("All VFCs are already submitted for this month.");
      return;
    }

    if (!window.confirm(`Submit ${selectedVfcs.length} VFC(s) for ${selectedMonth}?`))
      return;

    try {
      const config = Authorization();
      const payload = { month: selectedMonth, vfcIds: selectedVfcs };
      const res = await axios.post(
        `${Api}/api/declaration/range/submit`,
        payload,
        config
      );

      toast.success(res.data.message || "VFCs submitted successfully!");
      onClose();
    } catch (error) {
      console.error("Error submitting VFCs:", error);
      toast.error(error.response?.data?.error || "Submission failed");
    }
  };

  return (
    <Modal show={open} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Range Approval</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center my-3">
            {loading && <Preloader loading={loading} />}
          </div>
        ) : (
          <>
            <Alert variant="primary" className="text-center">
              By selecting the VFC(s) within your range, you hereby confirm that all records
              pertaining to these VFCs for the month <strong>{formatSelectedMonth()}</strong> have
              been duly updated in the web portal.
            </Alert>


            {/* ✅ Success alert if all VFCs are already submitted */}
            {allSubmitted && (
              <Alert variant="success" className="text-center">
                ✅ All VFCs for <strong>{selectedMonth}</strong> have already been
                submitted.
              </Alert>
            )}

            {/* ✅ Month display */}
            <Form.Group className="mb-3">
              <Form.Label>Selected Month</Form.Label>
              <Form.Control type="month" value={selectedMonth} disabled />
            </Form.Group>

            {/* ✅ JFMC TYPE BUTTONS */}
            {jfmcTypes.length > 0 && (
              <div className="d-flex gap-2 mb-3 justify-content-center">
                {jfmcTypes.map((type) => (
                  <div className="jfmc-switch">
                    <button
                      className={`btn ${selectedJfmcType?.jfmcTypeId === type.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
                      key={type.jfmcTypeId}
                      onClick={() => {
                        setSelectedJfmcType(type);
                        setSelectedVfcs([]);
                      }}
                    >
                      {type.jfmcTypeName}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ✅ Scrollable VFC list */}
            <div
              style={{
                maxHeight: "320px",
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              {vfcData.length === 0 ? (
                <p className="text-center text-muted">No VFCs available.</p>
              ) : (
                <>
                  {/* ✅ Select All */}
                  {!allSubmitted && (
                    <div className="d-flex justify-content-between align-items-center border-bottom py-2 bg-light px-2 rounded">
                      <Form.Check
                        type="checkbox"
                        id="selectAllVfcs"
                        label="Select All Unsubmitted VFCs"
                        checked={allSelected}
                        onChange={handleSelectAll}
                      />
                      <Badge bg={allSelected ? "success" : "secondary"}>
                        {allSelected
                          ? "All Selected"
                          : `${selectedVfcs.length}/${unsubmittedVfcs.length} Selected`}
                      </Badge>
                    </div>
                  )}

                  {/* ✅ List each VFC */}
                  {vfcData.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((vfc, index) => (
                    <div
                      key={vfc._id}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <div>
                        <Form.Check
                          type="checkbox"
                          label={`${index + 1}. ${vfc.vfcName}`}
                          value={vfc._id}
                          checked={selectedVfcs.includes(vfc._id)}
                          onChange={handleCheckboxChange}
                          disabled={vfc.submitted || allSubmitted}
                        />
                        {vfc.submittedAt && (
                          <small className="text-muted ms-4">
                            Submitted on{" "}
                            {new Date(vfc.submittedAt).toLocaleDateString()}
                          </small>
                        )}
                      </div>

                      <Badge bg={vfc.submitted ? "success" : "warning"}>
                        {vfc.submitted ? "Submitted" : "Pending"}
                      </Badge>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={selectedVfcs.length === 0 || allSubmitted}
        >
          Submit Selected
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RangeApproval;
